(() => {
  const configElement = document.querySelector('[data-pnp-bf-config]');
  if (!configElement) return;
  if (window.PnpBfCampaign?.refresh) {
    window.PnpBfCampaign.refresh();
    return;
  }
  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }
  const timestamp = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    if (typeof value === 'number' || /^\d+(?:\.\d+)?$/.test(String(value))) {
      const number = Number(value);
      return number < 1e12 ? number * 1000 : number;
    }
    return Date.parse(value);
  };
  const startsAt = timestamp(config.startsAt);
  const endsAt = timestamp(config.endsAt);
  const percentage = Number(config.percentage);
  const valid = Number.isFinite(startsAt) && Number.isFinite(endsAt) && endsAt > startsAt && percentage > 0 && percentage <= 100;
  const originalVisibility = new WeakMap();
  let active = null;
  let interval;
  let observerQueued = false;
  let refreshingCart = false;
  const remember = (element) => {
    if (!originalVisibility.has(element)) {
      originalVisibility.set(element, {
        hidden: element.hidden,
        display: element.style.getPropertyValue('display'),
        priority: element.style.getPropertyPriority('display'),
        ariaHidden: element.getAttribute('aria-hidden')
      });
    }
    return originalVisibility.get(element);
  };
  const hide = (element) => {
    remember(element);
    element.hidden = true;
    element.style.setProperty('display', 'none', 'important');
    element.setAttribute('aria-hidden', 'true');
  };
  const restoreStyle = (element, saved) => {
    if (saved.display) element.style.setProperty('display', saved.display, saved.priority);
    else element.style.removeProperty('display');
    if (saved.ariaHidden === null) element.removeAttribute('aria-hidden');
    else element.setAttribute('aria-hidden', saved.ariaHidden);
  };
  const showCampaignElement = (element) => {
    const saved = remember(element);
    restoreStyle(element, saved);
    element.hidden = false;
    element.removeAttribute('aria-hidden');
  };
  const syncElements = () => {
    document.querySelectorAll('[data-pnp-bf-campaign], [data-pnp-bf-sale-price]').forEach((element) => {
      if (active) showCampaignElement(element);
      else hide(element);
    });
    document.querySelectorAll('[data-pnp-bf-normal-price]').forEach((element) => {
      if (active) hide(element);
      else {
        const saved = remember(element);
        restoreStyle(element, saved);
        element.hidden = saved.hidden;
      }
    });
  };
  const formatDeadline = () => {
    if (!Number.isFinite(endsAt)) return;
    let deadline;
    try {
      deadline = new Intl.DateTimeFormat(config.locale || 'en-AU', {
        day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
        timeZone: config.timeZone || 'Australia/Perth'
      }).format(endsAt);
    } catch {
      deadline = new Date(endsAt).toISOString();
    }
    document.querySelectorAll('[data-pnp-bf-deadline]').forEach((element) => {
      element.dateTime = new Date(endsAt).toISOString();
      element.textContent = `${deadline}${config.dateSuffix ? ` ${config.dateSuffix}` : ''}`;
    });
  };
  const refreshCart = async () => {
    if (refreshingCart) return;
    const descriptors = [];
    ['main-cart-items', 'main-cart-footer'].forEach((id) => {
      const element = document.getElementById(id);
      if (element?.dataset.id) descriptors.push({ section: element.dataset.id, selector: `#${id} .js-contents` });
    });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const host = drawer.closest('[id^="shopify-section-"]');
      const section = host ? host.id.replace('shopify-section-', '') : 'cart-drawer';
      ['#CartDrawer-CartItems', '#CartDrawer .cart-drawer__footer'].forEach((selector) => {
        if (document.querySelector(selector)) descriptors.push({ section, selector });
      });
    }
    if (!descriptors.length) return;
    const focused = document.activeElement;
    if (focused?.matches('input, textarea, select') && focused.closest('cart-items, cart-drawer')) {
      focused.addEventListener('blur', () => refreshCart(), { once: true });
      return;
    }
    refreshingCart = true;
    try {
      const root = String(config.rootUrl || '/').replace(/\/?$/, '/');
      const url = new URL(`${root}cart`, window.location.origin);
      url.searchParams.set('sections', [...new Set(descriptors.map((descriptor) => descriptor.section))].slice(0, 5).join(','));
      url.searchParams.set('_pnp_bf_expiry', String(endsAt));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const sections = await response.json();
      descriptors.forEach(({ section, selector }) => {
        if (typeof sections[section] !== 'string' || !sections[section].trim()) return;
        const fragment = new DOMParser().parseFromString(sections[section], 'text/html');
        const next = fragment.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });
      syncElements();
      document.dispatchEvent(new CustomEvent('pnp:bf-cart-refreshed'));
    } catch {
      document.dispatchEvent(new CustomEvent('pnp:bf-cart-refresh-needed'));
    } finally {
      refreshingCart = false;
    }
  };
  const updateCountdown = (now) => {
    const remaining = Math.max(0, Math.ceil((endsAt - now) / 1000));
    const values = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor(remaining % 86400 / 3600),
      minutes: Math.floor(remaining % 3600 / 60),
      seconds: remaining % 60
    };
    Object.entries(values).forEach(([unit, value]) => {
      const formatted = String(value).padStart(2, '0');
      document.querySelectorAll(`[data-pnp-bf-${unit}]`).forEach((element) => {
        if (element.textContent !== formatted) element.textContent = formatted;
      });
    });
  };
  const update = () => {
    const now = Date.now();
    const next = valid && now >= startsAt && now < endsAt;
    const wasActive = active;
    if (next !== active) {
      active = next;
      document.documentElement.classList.toggle('pnp-bf-active', active);
      syncElements();
      document.dispatchEvent(new CustomEvent('pnp:bf-state', { detail: { active, percentage } }));
      if (wasActive === true && !active && now >= endsAt) {
        document.dispatchEvent(new CustomEvent('pnp:bf-expired', { detail: { endsAt, percentage } }));
        refreshCart();
        window.setTimeout(() => window.location.reload(), 3000);
      }
    }
    if (active) updateCountdown(now);
    if (valid && now >= endsAt && interval) {
      clearInterval(interval);
      interval = undefined;
    }
  };
  window.PnpBfCampaign = {
    get active() { return active === true; },
    percentage,
    startsAt,
    endsAt,
    serverNow: timestamp(config.serverNow),
    refresh: () => { update(); syncElements(); formatDeadline(); }
  };
  formatDeadline();
  update();
  if (valid && Date.now() < endsAt) interval = window.setInterval(update, 1000);
  window.addEventListener('focus', update);
  window.addEventListener('pageshow', () => { update(); syncElements(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) update(); });
  document.addEventListener('shopify:section:load', () => { update(); syncElements(); formatDeadline(); });
  const observer = new MutationObserver((records) => {
    const relevant = records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches('[data-pnp-bf-campaign], [data-pnp-bf-sale-price], [data-pnp-bf-normal-price]') || node.querySelector('[data-pnp-bf-campaign], [data-pnp-bf-sale-price], [data-pnp-bf-normal-price]'))));
    if (!relevant || observerQueued) return;
    observerQueued = true;
    queueMicrotask(() => { observerQueued = false; update(); syncElements(); formatDeadline(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

